import React, { useCallback, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { useIntl } from "react-intl";
import {
  Row,
  Col,
  Form,
  Input,
  Modal,
  Select,
  Checkbox,
  InputNumber,
} from "antd";

import InfoLabel from "../../app/generic/infoLabel";
import CustomIcon from "../../app/generic/customIcon";
import TextAreaItem from "../../app/generic/textAreaItem";
import CheckboxItem from "../../app/generic/checkboxItem";
import RadioGroupItem from "../../app/generic/radioGroupItem";
import InputNumberItem from "../../app/generic/inputNumberItem";

import "./index.css";

const { Item } = Form;
const { Option } = Select;
const { confirm } = Modal;

const WRAPPING_PROPS = {
  labelCol: { span: 14 },
  wrapperCol: { span: 10 },
  className: "wrapping-item",
};

function ColliModal({
  form,
  colli,
  rules,
  saving,
  visible,
  onCancel,
  wrappings,
  onOkClick,
  onWrappingWarnClick,
}) {
  const intl = useIntl();
  const { getFieldDecorator } = form;
  const formItemLayout = {
    labelCol: {
      span: 10,
    },
    wrapperCol: {
      span: 14,
    },
    colon: false,
  };

  const [manual, setManual] = useState(colli && colli.barcode);
  const onManualChange = useCallback(
    (e) => {
      if (e.target.checked) {
        confirm({
          title: intl.formatMessage({
            id: "messages.warning",
            defaultMessage: "Upozornění",
          }),
          content: intl.formatMessage({
            id: "consignment.colli.barcodeWarning",
            defaultMessage:
              "Opravdu si přejete zadat číslo kusu manuálně? Musíte vlastnit číselnou řadu a vložit neduplicitní číslo.",
          }),
          okText: intl.formatMessage({
            id: "messages.yes",
            defaultMessage: "Ano",
          }),
          okType: "danger",
          cancelText: intl.formatMessage({
            id: "messages.no",
            defaultMessage: "Ne",
          }),
          onOk() {
            setManual(e.target.checked);
          },
        });
      } else {
        setManual(e.target.checked);
        form.setFieldsValue({ barcode: undefined });
      }
    },
    [intl, form],
  );

  const onWeightPartChange = useCallback(() => {
    setTimeout(() => form.validateFields(["weight"]), 500);
  }, [form]);

  const maxLength = rules && rules.colliLength,
    maxWidth = rules && rules.colliWidth,
    maxHeight = colli && colli.height,
    maxWeight =
      form.getFieldValue("weightType") === 1
        ? rules.colliWeight
        : Math.min(
            rules.weight,
            form.getFieldValue("count") * rules.colliWeight,
          );

  const onPropertyChange = useCallback(
    (maxValue, value, property) => {
      value > maxValue &&
        console.log({
          title: intl.formatMessage({
            id: "messages.warning",
            defaultMessage: "Upozornění",
          }),
          content: property.replace("{max}", maxValue),
        });
    },
    [intl],
  );

  const handleCallback = useDebouncedCallback((maxValue, value, property) => {
    onPropertyChange(maxValue, value, property);
  }, 1000);

  return (
    <Modal
      width={920}
      onOk={onOkClick}
      visible={visible}
      cancelText={intl.formatMessage({
        id: "messages.cancel",
        defaultMessage: "Zrušit",
      })}
      onCancel={onCancel}
      confirmLoading={saving}
      okText={
        colli
          ? intl.formatMessage({
              id: "messages.save",
              defaultMessage: "Uložit",
            })
          : intl.formatMessage({
              id: "messages.insert",
              defaultMessage: "Vložit",
            })
      }
      title={
        colli
          ? intl.formatMessage({
              id: "consignment.colli.edit",
              defaultMessage: "Editace kusu",
            })
          : intl.formatMessage({
              id: "consignment.colli.add",
              defaultMessage: "Zadání nového kusu",
            })
      }
    >
      <Form {...formItemLayout}>
        <Row>
          <Col span={10}>
            <Item
              label={intl.formatMessage({
                id: "consignment.colli.count",
                defaultMessage: "Počet kusů",
              })}
            >
              {getFieldDecorator("count", {
                initialValue: 1,
                rules: [
                  {
                    required: true,
                    message: intl.formatMessage({
                      id: "consignment.colli.emptyCount",
                      defaultMessage: "Zadejte prosím počet kusů!",
                    }),
                  },
                ],
              })(
                <InputNumber
                  min={1}
                  precision={0}
                  max={rules.count}
                  onChange={onWeightPartChange}
                />,
              )}
            </Item>
            <Row type="flex">
              <Col span={rules.wrappings ? 22 : 24}>
                <Item
                  label={intl.formatMessage({
                    id: "consignment.colli.wrapping",
                    defaultMessage: "Druh obalu",
                  })}
                  {...(rules.wrappings ? WRAPPING_PROPS : {})}
                >
                  {getFieldDecorator("wrappingId", {
                    initialValue: colli && colli.wrappingId,
                    rules: [
                      {
                        required: true,
                        message: intl.formatMessage({
                          id: "consignment.colli.emptyWrapping",
                          defaultMessage: "Vyberte prosím druh obalu!",
                        }),
                      },
                    ],
                  })(
                    <Select
                      allowClear
                      showSearch
                      filterOption={(inputValue, option) =>
                        inputValue &&
                        option &&
                        option.props &&
                        option.props.children &&
                        option.props.children
                          .toUpperCase()
                          .indexOf(inputValue.toUpperCase()) !== -1
                      }
                    >
                      {wrappings.map((w, i) => (
                        <Option value={w.id} key={i}>
                          {w.name}
                        </Option>
                      ))}
                    </Select>,
                  )}
                </Item>
              </Col>
              {rules.wrappings && (
                <Col span={2} className="wrapping-icon">
                  <CustomIcon
                    size={25}
                    type="warning"
                    className="pointer"
                    onClick={() => onWrappingWarnClick(true)}
                    title={intl.formatMessage({
                      id: "consignment.colli.wrapping.info",
                      defaultMessage:
                        "Omezení druhů obalu, kliknutím zobrazíte více",
                    })}
                  />
                </Col>
              )}
            </Row>
            <Input.Group className="colli-modal-dimension-group">
              <InputNumberItem
                fieldName="length"
                label={intl.formatMessage({
                  id: "consignment.colli.length",
                  defaultMessage: "Délka [cm/ks]",
                })}
                formItemLayout={formItemLayout}
                message={intl.formatMessage({
                  id: "consignment.colli.emptyLength",
                  defaultMessage: "Zadejte prosím délku!",
                })}
                min={rules?.colliMinLength ?? 1}
                max={maxLength}
                initialValue={colli && colli.length}
                getFieldDecorator={getFieldDecorator}
                rangeMessage={
                  rules?.colliMinLength
                    ? undefined
                    : intl.formatMessage({
                        id: "consignment.colli.overLength",
                        defaultMessage: "Překročen max. rozměr {max} cm!",
                      })
                }
                onInputChange={(value) =>
                  handleCallback(
                    maxLength,
                    value,
                    intl.formatMessage(
                      {
                        id: "consignment.colli.overLength",
                        defaultMessage: "Překročen max. rozměr {max} cm!",
                      },
                      { max: maxLength },
                    ),
                  )
                }
              />
              <InputNumberItem
                fieldName="width"
                label={intl.formatMessage({
                  id: "consignment.colli.width",
                  defaultMessage: "Šířka [cm/ks]",
                })}
                formItemLayout={formItemLayout}
                message={intl.formatMessage({
                  id: "consignment.colli.emptyWidth",
                  defaultMessage: "Zadejte prosím šířku!",
                })}
                min={rules?.colliMinWidth ?? 1}
                max={maxWidth}
                initialValue={colli && colli.width}
                getFieldDecorator={getFieldDecorator}
                rangeMessage={
                  rules?.colliMinWidth
                    ? undefined
                    : intl.formatMessage({
                        id: "consignment.colli.overWidth",
                        defaultMessage: "Překročen max. rozměr {max} cm!",
                      })
                }
                onInputChange={(value) =>
                  handleCallback(
                    maxWidth,
                    value,
                    intl.formatMessage(
                      {
                        id: "consignment.colli.overWidth",
                        defaultMessage: "Překročen max. rozměr {max} cm!",
                      },
                      { max: maxWidth },
                    ),
                  )
                }
              />
              <InputNumberItem
                fieldName="height"
                label={intl.formatMessage({
                  id: "consignment.colli.height",
                  defaultMessage: "Výška [cm/ks]",
                })}
                formItemLayout={formItemLayout}
                message={intl.formatMessage({
                  id: "consignment.colli.emptyHeight",
                  defaultMessage: "Zadejte prosím výšku!",
                })}
                min={rules?.colliMinHeight ?? 1}
                max={rules && rules.colliHeight}
                initialValue={maxHeight}
                getFieldDecorator={getFieldDecorator}
                rangeMessage={
                  rules?.colliMinHeight
                    ? undefined
                    : intl.formatMessage({
                        id: "consignment.colli.overHeight",
                        defaultMessage: "Překročen max. rozměr {max} cm!",
                      })
                }
                onInputChange={(value) =>
                  handleCallback(
                    maxHeight,
                    value,
                    intl.formatMessage(
                      {
                        id: "consignment.colli.overHeight",
                        defaultMessage: "Překročen max. rozměr {max} cm!",
                      },
                      { max: maxHeight },
                    ),
                  )
                }
              />
            </Input.Group>
            <CheckboxItem
              fieldName="stackable"
              label={intl.formatMessage({
                id: "consignment.colli.stackable",
                defaultMessage: "Stohovatelné",
              })}
              formItemLayout={formItemLayout}
              getFieldDecorator={getFieldDecorator}
              initialValue={colli ? colli.stackable : true}
            />
          </Col>
          <Col span={14}>
            <Row type="flex" align="middle">
              <Col span={16}>
                <Item
                  label={intl.formatMessage({
                    id: "consignment.colli.weight",
                    defaultMessage: "Hmotnost [kg]",
                  })}
                  className="weight-item"
                  labelCol={{ span: 14 }}
                  wrapperCol={{ span: 10 }}
                >
                  {getFieldDecorator("weight", {
                    initialValue:
                      colli && colli.weight === 0 ? 1 : colli && colli.weight,
                    rules: [
                      {
                        required: true,
                        message: intl.formatMessage({
                          id: "consignment.colli.emptyWeight",
                          defaultMessage: "Zadejte prosím váhu!",
                        }),
                      },
                      {
                        min: rules?.colliMinWeight ?? 1,
                        max: maxWeight,
                        message: intl.formatMessage(
                          {
                            id: "consignment.colli.overWeight",
                            defaultMessage: "Překročena maximální váha {max}!",
                          },
                          {
                            max:
                              form.getFieldValue("weightType") === 1
                                ? rules.colliWeight
                                : Math.min(
                                    rules.weight,
                                    form.getFieldValue("count") *
                                      rules.colliWeight,
                                  ),
                          },
                        ),
                        type: "number",
                      },
                    ],
                  })(
                    <InputNumber
                      min={1}
                      precision={0}
                      onChange={(value) =>
                        handleCallback(
                          maxWeight,
                          value,
                          intl.formatMessage(
                            {
                              id: "consignment.colli.overWeight",
                              defaultMessage:
                                "Překročena maximální váha {max}!",
                            },
                            { max: maxWeight },
                          ),
                        )
                      }
                    />,
                  )}
                </Item>
              </Col>
              <Col span={8}>
                <RadioGroupItem
                  vertical
                  initialValue={1}
                  fieldName="weightType"
                  onChange={onWeightPartChange}
                  formItemLayout={formItemLayout}
                  getFieldDecorator={getFieldDecorator}
                  labels={[
                    intl.formatMessage({
                      id: "consignment.colli.weightType1",
                      defaultMessage: "Hmotnost 1 kusu",
                    }),
                    <InfoLabel
                      label={intl.formatMessage({
                        id: "consignment.colli.weightTypeAll",
                        defaultMessage: "Hmotnost všech kusů",
                      })}
                      info={intl.formatMessage({
                        id: "consignment.colli.weightTypeAllInfo",
                        defaultMessage:
                          "váha bude rozpočítána poměrem na jednotlivé kusy",
                      })}
                    />,
                  ]}
                  secondDisabled={form.getFieldValue("count") === 1}
                />
              </Col>
            </Row>
            <Row>
              <TextAreaItem
                allowClear
                fieldName="description"
                formItemLayout={formItemLayout}
                getFieldDecorator={getFieldDecorator}
                initialValue={colli && colli.description}
                message={intl.formatMessage({
                  id: "consignment.colli.emptyDescription",
                  defaultMessage: "Zadejte prosím druh zboží!",
                })}
                label={intl.formatMessage({
                  id: "consignment.colli.description",
                  defaultMessage: "Druh zboží",
                })}
                autoSize={{ minRows: 3, maxRows: 8 }}
              />
            </Row>
            <Row>
              <Item
                className="colli-modal-textarea"
                wrapperCol={{ offset: 10 }}
              >
                <Checkbox checked={manual} onChange={onManualChange}>
                  {intl.formatMessage({
                    id: "consignment.colli.manualBarcode",
                    defaultMessage: "manuálně zadat číslo kusu",
                  })}
                </Checkbox>
              </Item>
              <Item
                label={intl.formatMessage({
                  id: "consignment.colli.barcode",
                  defaultMessage: "Číslo kusu",
                })}
              >
                {getFieldDecorator("barcode", {
                  initialValue: colli && colli.barcode,
                  rules: [
                    {
                      pattern:
                        "^([1-8]?[A-Z]{2,3}[A-Z0-9]{1,35})|([1-8]?J[A-Z]{2,3}[A-Z0-9]{1,35})|(\\d{18})|(00\\d{18})$",
                      message: intl.formatMessage({
                        id: "consignment.colli.invalidBarcode",
                        defaultMessage: "Zadejte validní formát čísla kusu!",
                      }),
                    },
                  ],
                })(<Input allowClear disabled={!manual} />)}
              </Item>
            </Row>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}

export default Form.create()(ColliModal);
